import React, { FC, useCallback, useState } from "react";
import { Grid } from "@mui/material";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import { useMasterTables } from "../../../../../../stores/MasterTables";
import BillBoxWrapper from "../../common/components/BillBoxWrapper";
import ScrollablePanel from "../../common/components/ScrollablePanel";
import TransactionRxOverview from "../../../Base/components/TransactionRxOverview";
import { GkvNewBillPageProps } from "../types/interface";
import { useGkvBills } from "../hooks/useGkvBills";
import GkvBillWorkspace from "./GkvBillWorkspace";
import { GkvEditPage } from "../../GKVEdit";
import { colors } from "../styles/tokens";

/**
 * Top-level page for the GKV New Bill workflow.
 *
 * After bills are created, automatically transitions to GKVEdit
 * so the user can continue with taxieren/print/export in one flow.
 */
const GkvNewBillPage: FC<GkvNewBillPageProps> = ({
  handleFullScreenDialogClose,
  selectedAccount,
  userName,
  userId,
}) => {
  const gkvBills = useGkvBills(selectedAccount);
  const { data: masterTablesData } = useMasterTables();

  const [selectedRx, setSelectedRx] = useState<any>(null);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [editRxMode, setEditRxMode] = useState(false);
  const [editBillIds, setEditBillIds] = useState<string[] | null>(null);

  /** Called when a row in a bill table is clicked */
  const handleRowClick = (rx: any) => {
    setSelectedRx(rx);
    setIsDetailVisible(true);
  };

  /** Called when the selected Rx is removed from the bill list */
  const handleRxDelete = (deletedRxId: string) => {
    if (selectedRx?.id === deletedRxId) {
      setSelectedRx(null);
      setIsDetailVisible(false);
    }
  };

  /** Called after filterValid — clear selection if it was removed */
  const handleValidationFilter = (remainingRxIds: string[]) => {
    if (selectedRx && !remainingRxIds.includes(selectedRx.id)) {
      setSelectedRx(null);
      setIsDetailVisible(false);
    }
  };

  /** After bills are saved, transition to GKVEdit with created IDs */
  const handleBillsSaved = useCallback((createdIds: string[]) => {
    setEditBillIds(createdIds);
  }, []);

  /** When edit view closes, close the fullscreen dialog */
  const handleEditClose = useCallback(() => {
    setEditBillIds(null);
    handleFullScreenDialogClose();
  }, [handleFullScreenDialogClose]);

  // ── Edit mode: show GkvEditPage ──
  if (editBillIds) {
    return <GkvEditPage billIds={editBillIds} onClose={handleEditClose} />;
  }

  // ── New mode: show workspace ──
  return (
    <Grid container spacing={1} sx={{ height: "100%", marginTop: "1px", overflow: "hidden" }}>
      {/* Left panel — Bill workspace */}
      <BillBoxWrapper xs={5}>
        <TopLevelPaper
          sx={{
            padding: 0,
            backgroundColor: colors.rowHover,
            height: "calc(100vh - 64px)",
            overflow: "hidden",
          }}
        >
          <ScrollablePanel sx={{ padding: "0 0 12px 12px" }}>
            <GkvBillWorkspace
              gkvBills={gkvBills}
              account={selectedAccount}
              userName={userName}
              userId={userId}
              onRowClick={handleRowClick}
              onRxDelete={handleRxDelete}
              onValidationFilter={handleValidationFilter}
              onBillsSaved={handleBillsSaved}
              handleFullScreenDialogClose={handleFullScreenDialogClose}
            />
          </ScrollablePanel>
        </TopLevelPaper>
      </BillBoxWrapper>

      {/* Right panel — Rx detail overview */}
      {isDetailVisible && (
        <BillBoxWrapper xs={7}>
          <TopLevelPaper sx={{ padding: 0, height: "calc(100vh - 64px)", overflow: "hidden" }}>
            <ScrollablePanel sx={{ padding: "12px" }}>
              <TransactionRxOverview
                rowData={selectedRx}
                masterData={masterTablesData}
                editRxMode={editRxMode}
                setEditRxMode={setEditRxMode}
              />
            </ScrollablePanel>
          </TopLevelPaper>
        </BillBoxWrapper>
      )}
    </Grid>
  );
};

export default GkvNewBillPage;
